/**
 * Function Card Component - Display function in marketplace with all key information
 */

import React from 'react';
import { FunctionListing } from '../../services/marketplace';
import { StarIcon, HeartIcon, ArrowDownTrayIcon, EyeIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';

interface FunctionCardProps {
  function: FunctionListing;
  onPurchase?: (functionId: string) => void;
  onWishlist?: (functionId: string, add: boolean) => void;
  onPreview?: (functionId: string) => void;
  isWishlisted?: boolean;
  className?: string;
}

export const FunctionCard: React.FC<FunctionCardProps> = ({
  function: func,
  onPurchase,
  onWishlist,
  onPreview,
  isWishlisted = false,
  className = ''
}) => {
  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(price);
  };

  const getPricingDisplay = () => {
    switch (func.pricing.model) {
      case 'free':
        return <span className="text-green-600 font-semibold">Free</span>;
      case 'paid':
        return <span className="text-blue-600 font-semibold">{formatPrice(func.pricing.price || 0, func.pricing.currency)}</span>;
      case 'subscription':
        return (
          <span className="text-purple-600 font-semibold">
            {formatPrice(func.pricing.price || 0, func.pricing.currency)}
            <span className="text-sm text-gray-500">/{func.pricing.billingCycle}</span>
          </span>
        );
      case 'usage':
        return (
          <div className="text-orange-600 font-semibold">
            <div>{formatPrice(func.pricing.usagePrice || 0, func.pricing.currency)}/call</div>
            {func.pricing.freeLimit && (
              <div className="text-xs text-gray-500">{func.pricing.freeLimit} free calls</div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  const getRatingStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <StarIcon
        key={i}
        className={`w-4 h-4 ${i < Math.floor(rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
      />
    ));
  };

  return (
    <div
      className={`bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden ${className}`}
      data-testid={`marketplace-function-card-${func.id}`}
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg font-semibold text-gray-900 truncate">{func.name}</h3>
              {func.featured && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  Featured
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 line-clamp-2 mb-2">{func.description}</p>

            {/* Author Info */}
            <div className="flex items-center gap-2 mb-2">
              {func.author.avatar && (
                <img
                  src={func.author.avatar}
                  alt={func.author.displayName}
                  className="w-5 h-5 rounded-full"
                />
              )}
              <span className="text-sm text-gray-600">{func.author.displayName}</span>
              {func.author.verified && (
                <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-1 mb-2">
              {func.tags.slice(0, 3).map((tag) => (
                <span key={tag} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {tag}
                </span>
              ))}
              {func.tags.length > 3 && (
                <span className="text-xs text-gray-500">+{func.tags.length - 3} more</span>
              )}
            </div>
          </div>

          {/* Wishlist Button */}
          <button
            onClick={() => onWishlist?.(func.id, !isWishlisted)}
            className="ml-2 p-2 text-gray-400 hover:text-red-500 transition-colors"
            title={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            {isWishlisted ? (
              <HeartSolidIcon className="w-5 h-5 text-red-500" />
            ) : (
              <HeartIcon className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="px-4 py-3 bg-gray-50">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              {getRatingStars(func.stats.rating)}
              <span className="ml-1">{func.stats.rating.toFixed(1)}</span>
              <span className="text-gray-400">({func.stats.reviewCount})</span>
            </div>
            <div className="flex items-center gap-1">
              <ArrowDownTrayIcon className="w-4 h-4" />
              <span>{func.stats.downloads.toLocaleString()}</span>
            </div>
          </div>
          <div className="text-xs text-gray-500">
            v{func.version} • {new Date(func.stats.lastUpdated).toLocaleDateString()}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            {getPricingDisplay()}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => onPreview?.(func.id)}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <EyeIcon className="w-4 h-4" />
              Preview
            </button>

            {func.pricing.model === 'free' ? (
              <button
                onClick={() => onPurchase?.(func.id)}
                className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                Get Free
              </button>
            ) : (
              <button
                onClick={() => onPurchase?.(func.id)}
                className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Purchase
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FunctionCard;
